import { Directive, ElementRef, OnDestroy, AfterViewInit, Inject, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

/**
 * Move o elemento hospedeiro para o `<body>` (portal), tirando-o do contexto de
 * empilhamento em que nasceu.
 *
 * Por quê: `global.scss` define `.tabs-content ion-content { position: relative;
 * z-index: 0 }`, o que cria um CONTEXTO DE EMPILHAMENTO. Qualquer overlay renderizado
 * dentro do `ion-content` fica confinado nele: por mais alto que seja o z-index interno,
 * ele nunca passa por cima de irmãos do content (`ion-footer` com os botões do carrinho,
 * barra de abas), porque quem disputa lá fora é o `ion-content` inteiro, com z-index 0.
 * Sintoma: bottom-sheet aparecendo POR BAIXO do rodapé "FINALIZAR PEDIDO" e backdrop que
 * não escurece a barra de abas.
 *
 * Uso: envolva só a parte de overlay (`position: fixed`) num elemento com a diretiva.
 * O Angular continua dono do elemento (bindings e eventos seguem funcionando) e o
 * encapsulamento de estilo é por atributo, então o CSS do componente continua valendo
 * fora da árvore original. Na destruição o nó é removido do body (sem vazamento).
 *
 * No servidor (SSR) não move nada: o HTML sai com o nó no lugar em que nasceu e o
 * cliente faz o portal depois de hidratar. Quem usa esta diretiva numa tela
 * pré-renderizada deve marcar o componente com `ngSkipHydration`, porque mover nó é
 * exatamente o tipo de mexida no DOM que a hidratação não sabe acompanhar.
 */
@Directive({
	selector: '[appBodyPortal]',
	standalone: true,
})
export class BodyPortalDirective implements AfterViewInit, OnDestroy {
	constructor(
		private el: ElementRef<HTMLElement>,
		@Inject(DOCUMENT) private doc: Document,
		@Inject(PLATFORM_ID) private platformId: Object,
	) {}

	ngAfterViewInit(): void {
		if (!isPlatformBrowser(this.platformId)) return;
		const node = this.el.nativeElement;

		if (node && node.parentNode !== this.doc.body) {
			this.doc.body.appendChild(node);
		}
	}

	ngOnDestroy(): void {
		const node = this.el.nativeElement;

		if (node && node.parentNode) {
			node.parentNode.removeChild(node);
		}
	}
}
